import { createContext, useContext, useEffect, useState, ReactNode } from "react";

export type ThemeName = "court" | "clay" | "daylight" | "grass" | "hardcourt";

const STORAGE_KEY = "tl-theme";
const THEME_CLASSES: Record<ThemeName, string> = {
  court: "",
  clay: "theme-clay",
  daylight: "theme-daylight",
  grass: "theme-grass",
  hardcourt: "theme-hardcourt",
};

interface ThemeContextValue {
  theme: ThemeName;
  setTheme: (t: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "hardcourt",
  setTheme: () => {},
});

function applyTheme(theme: ThemeName) {
  const root = document.documentElement;
  // remove all theme classes
  Object.values(THEME_CLASSES).forEach((cls) => {
    if (cls) root.classList.remove(cls);
  });
  const cls = THEME_CLASSES[theme];
  if (cls) root.classList.add(cls);
  // Daylight should drop the forced "dark" class so shadcn light tokens read naturally
  if (theme === "daylight") {
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  } else {
    root.classList.add("dark");
    root.style.colorScheme = "dark";
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (typeof window === "undefined") return "hardcourt";
    const stored = localStorage.getItem(STORAGE_KEY) as ThemeName | null;
    return stored && stored in THEME_CLASSES ? stored : "hardcourt";
  });

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeState }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
